import type React from 'react';
import type { FC } from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAppState } from 'state';

import useConfig from 'app/config';
import { isMobileDevice } from 'app/utils/is-mobile';

import Typography from 'app/ui/components/atoms/typography';
import Accordion from 'app/ui/components/molecules/accordion';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import VideoPlayer from 'app/ui/components/molecules/video-component';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import WorkoutActions from 'app/ui/components/molecules/workout-actions';
import Button from 'app/ui/components/atoms/button';

import { PageContainer, Footer, Wrapper, Navbar, HtmlTitle } from 'app/ui/components/layouts/generic-page';

import { EquipmentIconWithName, getEquipmentIconWithName } from 'app/ui/components/atoms/icons/equipment/util';
import { lengthFromDurationSeconds } from 'app/utils/duration-handler';

import IntensityIcon from 'app/ui/components/atoms/icons/workout/intensity';
import DurationIcon from 'app/ui/components/atoms/icons/workout/duration';
import PdfFooterIcon from 'app/assets/pdf-footer.svg';

import { WorkoutPage as WORKOUT } from 'app/pages/workout/workout.gql';
import type {
  WorkoutPageQuery,
  WorkoutPageQueryVariables,
  SaveLessonByIdMutation,
  SaveLessonByIdMutationVariables,
  UnsaveLessonByIdMutation,
  UnsaveLessonByIdMutationVariables,
  CompleteLessonByIdMutation,
  CompleteLessonByIdMutationVariables,
  UncompleteLessonByIdMutation,
  UncompleteLessonByIdMutationVariables,
} from 'app/types/graphql';

import type { ContentData } from 'app/ui/components/molecules/content-item-areas-wrapper';

import {
  type VideoData,
  type WorkoutType,
  type WorkoutVariant,
  isPdf,
  getVideoData,
  flattenSectionsToExercises,
} from 'app/pages/workout/util';

import DotDividedElementsWrapper from 'app/ui/components/molecules/dot-divided-elements-wrapper';

import { CompleteLessonById, UncompleteLessonById } from 'app/pages/workout/complete.gql';
import { SaveLessonById, UnsaveLessonById } from 'app/pages/workout/save.gql';

import { sectionToData } from 'app/pages/workout/util/data';
import { mapLessonToSlides } from 'app/ui/components/molecules/whiteboard-slide/util';

import Slideshow from 'app/ui/components/molecules/slideshow';
import { useAlternativeSelections } from 'app/hooks/use-alternative-exercises';
import { trackEvent } from 'src/app/utils/analytics/tracker';
import { useWorkoutDataContext, WorkoutDataProvider } from 'app/pages/workout/context';
import { getPathFromUrl, isInIframe } from 'src/app/utils/in-app-comunication/utils';
import { useCommunicationService } from 'src/app/services/communication';
import { MessageType } from 'src/app/utils/in-app-comunication/types';
import { generateWorkoutPdf } from 'src/app/utils/pdf/workout-pdf';
import { routes } from 'src/app/routes/constants';
import WhiteboardButton from 'src/app/ui/components/molecules/whiteboard-button';
import AcademyBanner from 'src/app/ui/components/molecules/academy-banner';

export type HandleSectionFocusChangeProps = {
  sectionKey?: string | null;
  sectionExerciseKey?: string | null;
  exerciseKey?: string | null;
};

type WorkoutPageProps = {
  workout: WorkoutType;
  pdfExportEnabled: boolean;
  contentItemFocused: string | null;
  focusedAccordionSection: string | null;
  setFocusedSection: (props: HandleSectionFocusChangeProps) => void;
  exercisePicked: string | null;
  userId: number;
  canAccessWorkoutBuilder: boolean;
  showAcademyPromo: boolean;
  variant?: WorkoutVariant;
};

type WorkoutPageWrapperProps = {
  lessonId: string;
  userId: number;
  variant?: WorkoutVariant;
};

type WorkoutBreakdownProps = {
  pdfExportEnabled: boolean;
  workout: WorkoutType;
  isSaved: boolean;
  isMigrated: boolean;
  saveAction: () => void;
  unSaveAction: () => void;
  isCompleted: boolean;
  completeAction: () => void;
  unCompleteAction: () => void;
  canAccessWorkoutBuilder: boolean;
  onPdfExport?: () => void;
  variant?: WorkoutVariant;
};

type InitialSectionKeys = {
  sectionKey: string;
  sectionExerciseKey: string | null;
};

type WorkoutAccordionProps = {
  canAccessWorkoutBuilder: boolean;
  pdfExportEnabled: boolean;
  data: ContentData[];
  initialSelectedKeys: InitialSectionKeys;
  updateVideoData: (key: string | null) => void;
  isSaved: boolean;
  saveAction: () => void;
  unSaveAction: () => void;
  isCompleted: boolean;
  completeAction: () => void;
  unCompleteAction: () => void;
  isMigrated: boolean;
  onPdfExport?: () => void;
  variant?: WorkoutVariant;
};

type WorkoutMediaProps = {
  videoData: VideoData | null;
  workoutImageUrl: string;
  isSticky: boolean;
  autoPlay?: boolean;
};

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => `
    padding: 0 0 ${spacing(8)} 0;
  `}

  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      display: flex;
      padding: ${spacing(8)};
    `,
  )}
  `};

  ${({ theme: { media, spacing } }) => `${media.large(
    () => `
      width: 95%;
      padding: 0 0 ${spacing(8)} 0;
    `,
  )}
  `};
`;

const WorkoutTopLayer = styled.div<{ $variant?: WorkoutVariant }>`
  display: flex;
  flex-direction: column;
  ${({ theme: { media }, $variant }) => `${media.medium(
    () => `
      max-width: ${$variant === 'pdf' ? '100%' : '60%'};
      flex: 3;
    `,
  )}
  `};
`;

const Breakdown = styled.div`
  word-break: break-word;
`;

const BreakdownWrapper = styled(Breakdown)`
  display: flex;
  flex-direction: column;
`;

const Video = styled.div`
  width: 80%;

  ${({ theme: { spacing } }) => `
    margin-bottom: ${spacing(8)};
  `}

  ${({ theme: { media } }) => `${media.medium(
    () => `
      margin: 0 auto;
    `,
  )}
  `};
`;

const Title = styled(Typography)`
  text-transform: uppercase;
`;

const Description = styled(Typography)`
  margin-bottom: 1rem;

  ${({ theme: { media, spacing } }) => `${media.medium(
    () => `
      padding-right: ${spacing(4)};
    `,
  )}
  `};
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(4)};
  `}

  label {
    text-transform: uppercase;
  }
`;

const EquipmentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(2)};
    margin-bottom: ${spacing(6)}
  `}
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;

  ${({ theme: { spacing } }) => `
    gap: ${spacing(2)};
    margin: ${spacing(4)} 0 ${spacing(6)};
  `}

  label {
    text-transform: uppercase;
  }
`;

const Icon = styled.div`
  ${({ theme: { spacing } }) => `
    width: ${spacing(4)};
    height: ${spacing(4)};
  `}

  align-items: baseline;

  img {
    width: 100%;
    height: 100%;
  }
`;

const StyledAccordion = styled(Accordion)`
  width: 100%;
  padding: 0 0.1rem;

  ${({ theme: { media } }) => `${media.medium(
    () => `
      z-index: 10;
      position: relative;
    `,
  )}
  `};
`;

const StyledVideoPlayer = styled(VideoPlayer)`
  aspect-ratio: 1;
  ${({ theme: { media } }) => `${media.medium(
    () => `
    aspect-ratio: 0.75;
    `,
  )}
  `};
`;

const StyledImage = styled.img`
  max-width: 100%;
  object-fit: cover;
  aspect-ratio: 1;
  ${({ theme: { spacing } }) => `border-radius: ${spacing(2)};`}
  ${({ theme: { media } }) => `${media.medium(
    () => `
      aspect-ratio: 0.75;
    `,
  )}
  `};
`;

const WorkoutMediaWrapper = styled.div<{ $isSticky: boolean }>`
  width: 100%;
  ${({ $isSticky }) => $isSticky && 'position: sticky;'}
  top: 4.75em;
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: fit-content;
  z-index: 2;

  ${({ theme: { colors } }) => `background: ${colors.black};`}

  ${({ theme: { media } }) => `${media.medium(
    () => `
      flex: 2;
      justify-content: flex-end;
      background: transparent;
      position: sticky;
    `,
  )}
  `};
`;

const DesktopStructure = styled.div`
  display: none;
  ${({ theme: { media } }) => `${media.medium(
    () => `
      display: flex;
      flex-direction: row;
    `,
  )}
  `};
`;

const MobileStructure = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme: { media } }) => `${media.medium(
    () => `
      display: none;
    `,
  )}
  `};
`;

const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledWorkoutActions = styled(WorkoutActions)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const SavePDFButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const PdfFooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const PdfFooterImage = styled.img`
  height: 40px;
  width: auto;
`;

const TopNavBar = styled(Navbar)<{ $hidden: boolean }>`
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

const lessonToWorkout = (lesson: any): WorkoutType => ({
  ...lesson,
  duration: lengthFromDurationSeconds(lesson.duration),
  imageUrl: lesson.image.url || lesson.image.signedUrl || '',
  intensity: typeof lesson.rpe === 'number' ? `RPE ${lesson.rpe}` : '',
  equipment: lesson.equipment || [],
  concept: lesson.concept?.name || '',
});

const WorkoutBreakdown: FC<WorkoutBreakdownProps> = ({
  workout,
  pdfExportEnabled,
  isSaved,
  isMigrated,
  saveAction,
  unSaveAction,
  isCompleted,
  completeAction,
  unCompleteAction,
  canAccessWorkoutBuilder,
  onPdfExport,
  variant = 'regular',
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const location = useLocation();
  const comms = useCommunicationService();

  const { config } = useConfig();
  const isWhiteboardEnabled = config.REACT_APP_WHITEBOARD_ENABLED;

  const selectedAlternatives = useAlternativeSelections();

  const queryParams = new URLSearchParams(location.search);
  const isPresenterView = queryParams.get('isPresenterView') !== 'false';
  const isSlideshowOn = queryParams.get('isSlideshowOn') === 'true';

  const [slides, setSlides] = useState<React.JSX.Element[]>(
    mapLessonToSlides({ workout, isPresenterView, selectedAlternatives }),
  );

  const [showWhiteboard, setShowWhiteboard] = useState(isSlideshowOn);

  const equipmentLabel = intl.formatMessage({ id: 'lesson.labels.equipment' });

  const handlePresentationButtonClick = () => {
    setSlides(mapLessonToSlides({ workout, isPresenterView, selectedAlternatives }));
    setShowWhiteboard(true);
    if (isInIframe()) {
      comms.sendMessage({
        type: MessageType.WHITEBOARD_OPEN,
        url: getPathFromUrl(window.location.href),
        workoutName: workout.name,
      });
    }
    trackEvent('whiteboard_opened', {
      workout_id: workout.id,
      workout_name: workout.name,
    });
  };

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.ATHLETE_VIEW_OPEN) {
        if (message.url) {
          window.location.href = message.url;
        }
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [comms]);

  return (
    <Breakdown>
      <Title variant="h2">{workout.name}</Title>
      <DetailsWrapper>
        <DetailWrapper>
          <Icon>
            <IntensityIcon />
          </Icon>
          <Typography variant="label">{workout.intensity}</Typography>
        </DetailWrapper>
        <DetailWrapper>
          <Icon>
            <DurationIcon />
          </Icon>
          <Typography variant="label">{workout.duration}</Typography>
        </DetailWrapper>
      </DetailsWrapper>
      {workout.equipment.length > 0 && (
        <EquipmentWrapper>
          <Typography variant="h6">{equipmentLabel}: </Typography>
          {
            <DotDividedElementsWrapper
              elements={workout.equipment
                .map((eq) =>
                  getEquipmentIconWithName({
                    name: eq.name,
                    color: theme.colors.white,
                  }),
                )
                .filter((icon) => !!icon)
                .map(
                  (details) =>
                    details && (
                      <EquipmentIconWithName
                        key={details.name}
                        icon={details.icon}
                        name={details.name}
                        color={theme.colors.white}
                      />
                    ),
                )}
              color={theme.colors.white}
            />
          }
        </EquipmentWrapper>
      )}
      <Description variant="body2" color={theme.colors.paragraphGray}>
        {workout.description}
      </Description>
      {!isPdf(variant) && (
        <>
          <StyledWorkoutActions
            pdfExportEnabled={pdfExportEnabled}
            isSaved={isSaved}
            isCompleted={isCompleted}
            saveAction={saveAction}
            unSaveAction={unSaveAction}
            completeAction={completeAction}
            unCompleteAction={unCompleteAction}
            canAccessWorkoutBuilder={canAccessWorkoutBuilder}
            isMigrated={isMigrated}
            onPdfExport={onPdfExport}
          />
          {isWhiteboardEnabled && (
            <WhiteboardButton onOpenWhiteboard={handlePresentationButtonClick} workout={workout} />
          )}
        </>
      )}

      {isWhiteboardEnabled && showWhiteboard && (
        <Slideshow
          isPresenterView={isPresenterView}
          slides={slides}
          onEndWhiteboard={() => setShowWhiteboard(false)}
          workout={workout}
        />
      )}
    </Breakdown>
  );
};

const WorkoutMedia: FC<WorkoutMediaProps> = ({
  videoData,
  workoutImageUrl,
  isSticky,
  autoPlay = true,
}: WorkoutMediaProps) => (
  <WorkoutMediaWrapper $isSticky={isSticky}>
    <Video>
      {videoData ? (
        <StyledVideoPlayer
          duration={videoData.duration}
          src={videoData.src}
          title={videoData.name}
          thumbnail={videoData.thumbnail}
          key={videoData.key}
          loop={true}
          className="workoutVideoPlayer"
          autoPlay={autoPlay}
        />
      ) : (
        <StyledImage src={workoutImageUrl} />
      )}
    </Video>
  </WorkoutMediaWrapper>
);

const WorkoutAccordion: FC<WorkoutAccordionProps> = ({
  data,
  pdfExportEnabled,
  initialSelectedKeys,
  updateVideoData,
  isSaved,
  isCompleted,
  isMigrated,
  saveAction,
  unSaveAction,
  completeAction,
  unCompleteAction,
  canAccessWorkoutBuilder,
  onPdfExport,
  variant = 'regular',
}: WorkoutAccordionProps) => {
  const initialExpandedKeys = isPdf(variant) ? data.map((item) => item.id) : undefined;

  return (
    <AccordionWrapper>
      <StyledAccordion
        data={data}
        className="workoutAccordion"
        initialSelectedKey={initialSelectedKeys.sectionKey}
        initialExpandedKeys={initialExpandedKeys}
        onClickEmit={updateVideoData}
        hideIcons={isPdf(variant)}
        textExpansionEnabled={!isPdf(variant)}
      />
      {!isPdf(variant) && (
        <WorkoutActions
          pdfExportEnabled={pdfExportEnabled}
          isSaved={isSaved}
          isCompleted={isCompleted}
          saveAction={saveAction}
          unSaveAction={unSaveAction}
          completeAction={completeAction}
          unCompleteAction={unCompleteAction}
          canAccessWorkoutBuilder={canAccessWorkoutBuilder}
          isMigrated={isMigrated}
          onPdfExport={onPdfExport}
        />
      )}
    </AccordionWrapper>
  );
};

const WorkoutPage: FC<WorkoutPageProps> = ({
  workout,
  pdfExportEnabled,
  contentItemFocused,
  focusedAccordionSection,
  exercisePicked,
  setFocusedSection,
  userId,
  canAccessWorkoutBuilder,
  showAcademyPromo,
  variant = 'regular',
}: WorkoutPageProps) => {
  const theme = useTheme();
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const pdfFooterRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const intl = useIntl();
  const isMobile = useMemo(() => isMobileDevice(), []);

  const allExercises = flattenSectionsToExercises(workout.sections);

  const videoDataFromAccordion = getVideoData({
    focusedContentItem: contentItemFocused,
    exercises: allExercises,
    exercisePicked,
  });

  const [videoData, setVideoData] = useState<VideoData | null>(videoDataFromAccordion);

  const updateVideoData = useCallback(
    (key: string | null) => setFocusedSection({ sectionKey: key, sectionExerciseKey: null }),
    [],
  );

  const mainSection = workout?.sections?.find((section) => section.isMain);
  const initialSelectedKeys: InitialSectionKeys = {
    sectionKey: mainSection?.id,
    sectionExerciseKey: null,
  };

  useEffect(() => {
    setFocusedSection(initialSelectedKeys);
  }, []);

  useEffect(() => {
    setVideoData(videoDataFromAccordion);
  }, [focusedAccordionSection, contentItemFocused, exercisePicked]);

  const accordionData =
    workout.sections?.map((lessonSection) =>
      sectionToData({
        lessonSection,
        contentItemFocused,
        handleContentItemClick: setFocusedSection,
        isMobile,
        theme,
        alternativeExercisesEnabled: !isPdf(variant),
        expandNotesTextByDefault: isPdf(variant),
        hideExerciseChevrons: isPdf(variant),
      }),
    ) || [];

  const [isSaved, setIsSaved] = useState(workout.isSaved);
  const isMigrated = false;

  const savePdfLabel = intl.formatMessage({ id: 'lesson.buttons.savePdf' });

  const [saveLesson] = useMutation<SaveLessonByIdMutation, SaveLessonByIdMutationVariables>(SaveLessonById, {
    onError: () => {
      setIsSaved(false);
    },
    onCompleted: () => {
      setIsSaved(true);
    },
  });

  const [unSaveLesson] = useMutation<UnsaveLessonByIdMutation, UnsaveLessonByIdMutationVariables>(UnsaveLessonById, {
    onError: () => {
      setIsSaved(true);
    },
    onCompleted: () => {
      setIsSaved(false);
    },
  });

  const [isCompleted, setIsCompleted] = useState(workout.isCompleted);

  const [completeLesson] = useMutation<CompleteLessonByIdMutation, CompleteLessonByIdMutationVariables>(
    CompleteLessonById,
    {
      onError: () => {
        setIsCompleted(false);
      },
      onCompleted: () => {
        setIsCompleted(true);
      },
    },
  );

  const [unCompleteLesson] = useMutation<UncompleteLessonByIdMutation, UncompleteLessonByIdMutationVariables>(
    UncompleteLessonById,
    {
      onError: () => {
        setIsCompleted(true);
      },
      onCompleted: () => {
        setIsCompleted(false);
      },
    },
  );

  if (!userId) {
    return <ErrorComponent />;
  }

  const handleSaveLesson = async () => {
    await saveLesson({ variables: { id: workout.id, input: { user: { id: userId } } } });
  };

  const handleUnsaveLesson = async () =>
    unSaveLesson({ variables: { id: workout.id, input: { user: { id: userId } } } });

  const handleCompleteLesson = async () => {
    await completeLesson({ variables: { id: workout.id, input: { user: { id: userId } } } });
  };

  const handleUncompleteLesson = async () =>
    unCompleteLesson({ variables: { id: workout.id, input: { user: { id: userId } } } });

  const handlePdfExport = async () => {
    if (isPdf(variant)) {
      // In PDF view, generate the PDF
      if (contentWrapperRef.current) {
        try {
          await generateWorkoutPdf({
            element: contentWrapperRef.current,
            workoutName: workout.name,
            footerElement: pdfFooterRef.current,
          });
        } catch (error) {
          console.error('Failed to generate PDF:', error);
        }
      }
    } else {
      // In regular view, navigate to PDF route
      navigate(routes.WORKOUT_PDF.replace(':id', workout.id));
    }
  };

  const breakdown = (
    <WorkoutBreakdown
      workout={workout}
      pdfExportEnabled={pdfExportEnabled}
      isSaved={isSaved}
      saveAction={handleSaveLesson}
      unSaveAction={handleUnsaveLesson}
      isCompleted={isCompleted}
      completeAction={handleCompleteLesson}
      unCompleteAction={handleUncompleteLesson}
      canAccessWorkoutBuilder={canAccessWorkoutBuilder}
      isMigrated={isMigrated}
      onPdfExport={handlePdfExport}
      variant={variant}
    />
  );

  const accordion = (
    <WorkoutAccordion
      pdfExportEnabled={pdfExportEnabled}
      data={accordionData}
      initialSelectedKeys={initialSelectedKeys}
      updateVideoData={updateVideoData}
      isSaved={isSaved}
      saveAction={handleSaveLesson}
      unSaveAction={handleUnsaveLesson}
      isCompleted={isCompleted}
      completeAction={handleCompleteLesson}
      unCompleteAction={handleUncompleteLesson}
      canAccessWorkoutBuilder={canAccessWorkoutBuilder}
      isMigrated={isMigrated}
      onPdfExport={handlePdfExport}
      variant={variant}
    />
  );

  const media = (
    <WorkoutMedia
      videoData={videoData}
      workoutImageUrl={workout.imageUrl}
      isSticky={!!contentItemFocused}
      autoPlay={!isMobile}
    />
  );

  const location = useLocation();
  const isPdfRoute = location.pathname.endsWith('/pdf');

  // Determine contentTitle: if on PDF route, use "PDF Preview", otherwise use workout name
  const contentTitle = isPdfRoute ? intl.formatMessage({ id: 'breadcrumb.pdf' }) : workout.name;

  // Pass workout ID as dynamic segment so it displays the workout name instead of the ID
  const dynamicSegments = workout.id ? { [workout.id]: workout.name } : undefined;
  return (
    <PageContainer>
      <HtmlTitle title={`Workout - ${workout.name}`} />
      {/* Hidden footer element for PDF export */}

      <TopNavBar $hidden={isPdf(variant)} />
      <Wrapper>
        <ContentWrapper>
          <Breadcrumb contentTitle={contentTitle} dynamicSegments={dynamicSegments} />
          {pdfExportEnabled && isPdf(variant) && (
            <SavePDFButtonWrapper>
              <Button onClick={handlePdfExport} label={savePdfLabel} type="curved-grey" />
            </SavePDFButtonWrapper>
          )}
          <div ref={contentWrapperRef}>
            <DesktopStructure>
              <WorkoutTopLayer $variant={variant}>
                <BreakdownWrapper>{breakdown}</BreakdownWrapper>
                {accordion}
              </WorkoutTopLayer>
              {!isPdf(variant) && media}
            </DesktopStructure>
            <MobileStructure>
              <WorkoutTopLayer $variant={variant}>
                <BreakdownWrapper>{breakdown}</BreakdownWrapper>
              </WorkoutTopLayer>
              {!isPdf(variant) && media}
              {accordion}
            </MobileStructure>
          </div>
          {isPdf(variant) && (
            <PdfFooterWrapper ref={pdfFooterRef}>
              <PdfFooterImage src={PdfFooterIcon} alt="PDF Footer" />
            </PdfFooterWrapper>
          )}
        </ContentWrapper>
        {!isPdf(variant) && showAcademyPromo && <AcademyBanner />}
      </Wrapper>
      {!isPdf(variant) && <Footer />}
    </PageContainer>
  );
};

export const WorkoutPageWrapper: FC<WorkoutPageWrapperProps> = ({
  lessonId,
  userId,
  variant = 'regular',
}: WorkoutPageWrapperProps) => {
  const { data, loading, error } = useQuery<WorkoutPageQuery, WorkoutPageQueryVariables>(WORKOUT, {
    variables: { id: lessonId },
  });
  const navigate = useNavigate();

  const [focusedAccordionSection, setFocusedAccordionSection] = useState<string | null>(null);
  const [contentItemFocused, setContentItemFocused] = useState<string | null>(null);
  const [exercisePicked, setExercisePicked] = useState<string | null>(null);
  const { setWorkout } = useWorkoutDataContext();

  useEffect(() => {
    if (!loading && data) {
      setWorkout(lessonToWorkout(data.lessonById));
    }
  }, [data, loading, setWorkout]);

  if (loading) {
    return <Loading />;
  }

  if (error || !data || !data.lessonById) {
    return <ErrorComponent />;
  }

  const workout = lessonToWorkout(data.lessonById);
  const pdfExportEnabled = !!data.lessonById.collections?.some(
    (collection) => collection.title.toLowerCase() === 'running',
  );

  if (!pdfExportEnabled && variant === 'pdf') {
    navigate(routes.WORKOUT.replace(':id', lessonId));
  }

  const handleSectionFocusChange = ({ sectionKey, sectionExerciseKey, exerciseKey }: HandleSectionFocusChangeProps) => {
    if (typeof sectionKey !== 'undefined') {
      setFocusedAccordionSection(sectionKey);
    }

    if (typeof sectionExerciseKey !== 'undefined') {
      setContentItemFocused(sectionExerciseKey);
    }

    if (typeof exerciseKey !== 'undefined') {
      setExercisePicked(exerciseKey);
    }
  };

  return (
    <WorkoutPage
      workout={workout}
      pdfExportEnabled={pdfExportEnabled}
      contentItemFocused={contentItemFocused}
      focusedAccordionSection={focusedAccordionSection}
      setFocusedSection={handleSectionFocusChange}
      exercisePicked={exercisePicked}
      userId={userId}
      // Offered to everyone: cloning copies the workout into a private draft owned by the person
      // who clicked, so it needs no permission (JS-9612 gated it on `Lesson:update`, which is an
      // editor-grade permission the consumer tier cannot hold — see one-fiit #1592).
      canAccessWorkoutBuilder={true}
      showAcademyPromo={data.lessonById.showAcademyPromo}
      variant={variant}
    />
  );
};

const Workout: FC = () => {
  const { id: lessonId } = useParams();
  const userId = useAppState(({ auth }) => auth.userId);

  if (!lessonId || !userId) {
    return <ErrorComponent />;
  }

  return (
    <WorkoutDataProvider>
      <WorkoutPageWrapper lessonId={lessonId} userId={userId} />
    </WorkoutDataProvider>
  );
};

export default Workout;
